import { useState, useMemo, useRef, useContext } from 'react'
import PropTypes from 'prop-types'

import { convertTraits } from '@aytm/app/stats/lib/query/traits'
import { getQuestionsOptionsList } from '@aytm/app/stats/page/comments/lib/utils'

import { OEContext } from '@aytm/app/stats/page/comments'

import SurveyHeaderImageResponses from '@aytm/app/stats/page/comments/image-responses/components/SurveyHeaderImageResponses'
import PageDataImageResponses from '@aytm/app/stats/page/comments/image-responses/PageDataImageResponses'
import useFetchDataImageResponses from '@aytm/app/stats/page/comments/image-responses/hooks/useFetchDataImageResponses'
import useImageResponses from '@aytm/app/stats/page/comments/image-responses/hooks/useImageResponses'

// styles
import '@aytm/app/stats/page/comments/styles/keyframes.scss'

import style from '@aytm/app/stats/page/comments/styles/index.module.scss'
import { DEFAULT_SELECTED_QUESTION } from '@aytm/app/stats/page/components/lib/constants'

const ImageResponses = props => {
  const {
    specialMode,
    surveyData,
    reportId,
    apiKey,
    reportParams = {},
    reportVirtual,
    reportSchema,
    loadRejectedCS,
    isShowHiddenFilters,
    isAdvancedFiltersDisabled,
    isSpecialsSortingDisabled,
    isFilterDuplicates,
    onFetch,
    onUpdateSchema,
    onFetchSchema,
    onFetchApiKey,
    onCreateFilter,
    onQuestionsListChange,
    onChangeFilterDuplicates,
    onSelectedQuestionChange
  } = props

  const { searchState, dispatch, dispatchCommentStatuses } = useContext(OEContext)

  const { pageData = [], disabled: readOnly } = searchState

  const [survey, setSurvey] = useState(
    Object.assign({}, surveyData.survey || {}, {
      traits: convertTraits(surveyData.survey || {}),
      codeBooks: (surveyData.survey || {}).codeBooks || []
    })
  )
  const [currentResponse, setCurrentResponse] = useState(null)
  const [responsesOptions, setResponsesOptions] = useState({
    survey,
    reportParams,
    reportVirtual,
    reportSchema,
    selectedQuestion: reportParams.selectedQuestion || DEFAULT_SELECTED_QUESTION
  })

  const responsesRef = useRef([])
  const showTraitsRef = useRef({})
  const responseOrderRef = useRef({})

  const searchStateRef = useRef()
  searchStateRef.current = searchState

  const responsesOptionsRef = useRef()
  responsesOptionsRef.current = responsesOptions

  const propsRef = useRef()
  propsRef.current = props
  const surveyRef = useRef()
  surveyRef.current = survey

  const isSpecialsSortingDisabledRef = useRef()
  isSpecialsSortingDisabledRef.current = isSpecialsSortingDisabled

  const questionsList = useMemo(
    () =>
      getQuestionsOptionsList(
        responsesOptions.survey,
        responsesOptions.reportVirtual,
        responsesOptions.reportSchema,
        responsesOptions.selectedQuestion,
        specialMode
      ),
    [responsesOptions, specialMode]
  )

  const [fetchData] = useFetchDataImageResponses({
    apiKey,
    dispatch,
    dispatchCommentStatuses,
    searchStateRef,
    propsRef,
    responsesOptionsRef,
    surveyData,
    survey,
    setSurvey,
    specialMode,
    onFetchSchema,
    onFetch,
    onFetchApiKey
  })

  useImageResponses({
    apiKey,
    dispatch,
    searchStateRef,
    reportParams,
    propsRef,
    responsesOptionsRef,
    surveyData,
    survey,
    specialMode,
    questionsList,
    responsesRef,
    reportVirtual,
    reportSchema,
    pageData,
    setSurvey,
    setResponsesOptions,
    onQuestionsListChange,
    onUpdateSchema
  })

  const onPageChange = page => {
    const request = {
      currentPage: page
    }
    dispatch({
      type: 'SET_REQUEST_PARAM_SET',
      request
    })
  }

  return (
    <div className={`${style['comments-container']} stats-Comments`}>
      <SurveyHeaderImageResponses
        reportId={reportId}
        readOnly={readOnly}
        specialMode={specialMode}
        pageData={responsesOptions.pageData}
        questionsList={questionsList}
        showTraitsRef={showTraitsRef}
        isAdvancedFiltersDisabled={isAdvancedFiltersDisabled}
        isSpecialsSortingDisabled={isSpecialsSortingDisabled}
        responseOrderRef={responseOrderRef}
        currentResponse={currentResponse}
        loadRejectedCS={loadRejectedCS}
        onCreateFilter={onCreateFilter}
        onSelectedQuestionChange={onSelectedQuestionChange}
        onUpdateSchema={onUpdateSchema}
        onPageChange={onPageChange}
        fetchData={fetchData}
      />
      <PageDataImageResponses
        reportId={reportId}
        apiKey={apiKey}
        responsesOptions={responsesOptions}
        setResponsesOptions={setResponsesOptions}
        responsesRef={responsesRef}
        showTraitsRef={showTraitsRef}
        responseOrderRef={responseOrderRef}
        isShowHiddenFilters={isShowHiddenFilters}
        isFilterDuplicates={isFilterDuplicates}
        currentResponse={currentResponse}
        setCurrentResponse={setCurrentResponse}
        onChangeFilterDuplicates={onChangeFilterDuplicates}
        onUpdateParams={propsRef.current.onUpdateParams}
        onPageChange={onPageChange}
      />
    </div>
  )
}

ImageResponses.propTypes = {
  specialMode: PropTypes.string,
  surveyData: PropTypes.object,
  reportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  apiKey: PropTypes.object,
  CSRF: PropTypes.string,
  questionId: PropTypes.string,
  loadRejectedCS: PropTypes.string,
  reportParams: PropTypes.object,
  reportVirtual: PropTypes.object,
  reportSchema: PropTypes.array,
  isAdmin: PropTypes.bool,
  isSurveyGuest: PropTypes.bool,
  isShowHiddenFilters: PropTypes.bool,
  isAdvancedFiltersDisabled: PropTypes.bool,
  isTagsManagementDisabled: PropTypes.bool,
  isSentimentsManagementDisabled: PropTypes.bool,
  isSpecialsSortingDisabled: PropTypes.bool,
  isSurveyOwner: PropTypes.bool,
  isFilterDuplicates: PropTypes.bool,
  onChangeFilterDuplicates: PropTypes.func,
  onFetch: PropTypes.func,
  onFetchSchema: PropTypes.func,
  onFetchApiKey: PropTypes.func,
  onCreateFilter: PropTypes.func,
  onUpdateParams: PropTypes.func,
  onUpdateSchema: PropTypes.func,
  onQuestionsListChange: PropTypes.func,
  onSelectedQuestionChange: PropTypes.func,
  onQAModeChange: PropTypes.func
}

ImageResponses.displayName = 'ImageResponses'

export default ImageResponses
